import { Box, CircularProgress, Stack } from '@mui/material'
import { FC, PointerEvent, useCallback, useEffect, useRef, useState } from 'react'
import { WHITE } from 'constants/styling/theme'
import { Size, ViewTransform, viewTransform } from './adjustGeometry'

import { AdjustDocument, unitOf } from './adjust.types'
import { drawAdjustView } from './adjustView'
import { logger } from 'utils/logging'
import { useAdjustEditor } from './AdjustEditor.context'
import { useAdjustPointer } from './useAdjustPointer.hook'
import { useTranslation } from 'react-i18next'

/**
 * The photo fills the canvas like UnifiedPreview's contain-fit image, so it does not move when switching from Stage,
 * Enhance or Erase. The crop tool keeps a margin so the frame's handles stay grabbable at the edges.
 */
const CROP_VIEW_PADDING = 24
/**
 * The Adjust editor's canvas: renders the document over the preview photo (cached colour and blur passes), through
 * the crop's rotation and flips, with the tool's overlay. `before` is the image Before / Compare show.
 */
export const AdjustCanvas: FC<{ before: CanvasImageSource | null }> = ({ before }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { base, doc, tool, zoom, pan, viewMode, split, selectedStickerId, assets, assetsVersion, getRenderer, blurMode, blurBrushSize } = useAdjustEditor()
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const viewRef = useRef<ViewTransform | null>(null)
  const composedRef = useRef<{ doc: AdjustDocument, assetsVersion: number, preview: HTMLCanvasElement, canvas: HTMLCanvasElement } | null>(null)
  const [viewport, setViewport] = useState<Size>({ width: 0, height: 0 })
  const pointer = useAdjustPointer(canvasRef, viewRef)
  const brushCursorRef = useRef<HTMLDivElement>(null)
  const isBrushCursor = tool === 'redact' && blurMode === 'brush' && viewMode === 'after'

  const hideBrushCursor = useCallback(() => {
    if (brushCursorRef.current) brushCursorRef.current.style.display = 'none'
  }, [])

  /** Moves the brush circle with the pointer; its diameter is the brush's on screen (zoom included) */
  const onPointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    pointer.onPointerMove(event)
    const circle = brushCursorRef.current
    const view = viewRef.current
    if (!circle) return
    if (!isBrushCursor || !base || !view) {
      circle.style.display = 'none'
      return
    }
    const rect = event.currentTarget.getBoundingClientRect()
    const diameter = blurBrushSize * unitOf(base.size.width, base.size.height) * view.scale
    circle.style.display = 'block'
    circle.style.width = `${diameter}px`
    circle.style.height = `${diameter}px`
    circle.style.transform = `translate(${event.clientX - rect.left - diameter / 2}px, ${event.clientY - rect.top - diameter / 2}px)`
  }

  // Leaving Brush (Area, another tool, Before / Compare) removes the circle at once
  useEffect(() => {
    if (!isBrushCursor) hideBrushCursor()
  }, [isBrushCursor, hideBrushCursor])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const measure = () => setViewport({ width: container.clientWidth, height: container.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // Ctrl/⌘ + wheel must not zoom the page: needs a non-passive listener
  const onWheel = pointer.onWheel
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.addEventListener('wheel', onWheel, { passive: false })
    return () => canvas.removeEventListener('wheel', onWheel)
  }, [onWheel])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!base || !canvas || !viewport.width || !viewport.height) return
    const frame = requestAnimationFrame(() => {
      try {
        const cached = composedRef.current
        const isFresh = cached && cached.doc === doc && cached.assetsVersion === assetsVersion && cached.preview === base.preview
        const composed = isFresh ? cached.canvas : getRenderer().compose(doc, base.preview, base.preview.width, base.preview.height, base.size, assets)
        composedRef.current = { doc, assetsVersion, preview: base.preview, canvas: composed }

        const pixelRatio = window.devicePixelRatio || 1
        const width = Math.round(viewport.width * pixelRatio)
        const height = Math.round(viewport.height * pixelRatio)
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width
          canvas.height = height
        }
        const showWholePhoto = tool === 'crop' && viewMode === 'after'
        const view = viewTransform({ crop: doc.crop, image: base.size, viewport, zoom, pan, showWholePhoto, padding: showWholePhoto ? CROP_VIEW_PADDING : 0 })
        viewRef.current = view
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        drawAdjustView({
          ctx,
          viewport,
          pixelRatio,
          view,
          imageSize: base.size,
          after: composed,
          before,
          mode: viewMode,
          split,
          showWholePhoto,
          selectedSticker: tool === 'sticker' ? doc.stickers.find(sticker => sticker.id === selectedStickerId) ?? null : null,
          labels: { before: t('lab.compare.before'), after: t('lab.compare.after') },
        })
      } catch (error) {
        logger.error('Adjust editor: render failed', error, { context: { component: 'PhotoStudio', action: 'adjustRender' } })
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [base, doc, viewport, zoom, pan, tool, viewMode, split, selectedStickerId, assets, assetsVersion, before, getRenderer, t])

  return (
    <Box ref={containerRef} sx={{ position: 'relative', flex: '1 1 0%', minHeight: 0, width: '100%', overflow: 'hidden' }}>

      <Box
        component="canvas"
        ref={canvasRef}
        tabIndex={0}
        aria-label={t('adjust.canvas_label')}
        onPointerDown={pointer.onPointerDown}
        onPointerMove={onPointerMove}
        onPointerLeave={hideBrushCursor}
        onPointerUp={pointer.onPointerUp}
        onPointerCancel={pointer.onPointerUp}
        onKeyDown={pointer.onKeyDown}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', touchAction: 'none', outline: 'none' }}
      />

      {!base && (
        <Stack sx={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <CircularProgress size={32} sx={{ color: WHITE }} />
        </Stack>
      )}

      {/* Blur brush: a circle the size of the brush follows the pointer (a CSS cursor image is capped at 128 px) */}
      <Box
        ref={brushCursorRef}
        aria-hidden
        sx={{ position: 'absolute', top: 0, left: 0, display: 'none', borderRadius: '50%', border: `1.5px solid ${WHITE}`, boxShadow: '0 0 0 1px rgba(19, 24, 29, 0.6), inset 0 0 0 1px rgba(19, 24, 29, 0.6)', pointerEvents: 'none', willChange: 'transform' }}
      />

    </Box>
  )
}
